import type { HTMLAttributes } from 'react';

import React from 'react';

import {
  FloatingPortal,
  Placement,
  autoUpdate,
  flip,
  hide,
  offset,
  size,
  useFloating,
  UseFloatingOptions,
  FloatingPortalProps,
} from '@floating-ui/react';

import { classNames } from '../../utils/class-names';

import styles from './dropdown.module.css';
import { useOnClickOutside } from '../../hooks/use-on-click-outside';

const MAX_DROPDOWN_HEIGHT = 360;

export type DropdownPlacement = Placement;

export type DropdownProps = {
  testId?: string;
  isOpen: boolean;
  referenceElement: Element | null;
  className?: string;
  placement?: DropdownPlacement;
  offsetNr?: number;
  maxHeight?: number;
  forceWidthAsReference?: boolean;
  closeOnClickOutside?: boolean;
  usePortal?: boolean;
  dropdownOptions?: UseFloatingOptions;
  portalProps?: FloatingPortalProps;
  handleClose?: () => void;
} & HTMLAttributes<HTMLDivElement>;

/**
 * @param {string} [testId] - Test ID for the dropdown component.
 * @param {boolean} [isOpen] - Flag to control the visibility of the dropdown.
 * @param {Element | null} [referenceElement] - The reference element to which the dropdown is anchored.
 * @param {string} [className] - Additional class name for the dropdown.
 * @param {DropdownPlacement} [placement] - The placement of the dropdown relative to the reference element.
 * @default 'bottom-start'
 * @param {number} [offsetNr] - The offset distance from the reference element.
 * @default 0
 * @param {number} [maxHeight] - The maximum height of the dropdown.
 * @default 360
 * @param {boolean} [forceWidthAsReference] - Flag to match the width of the dropdown to the reference element.
 * @param {boolean} [closeOnClickOutside] - Flag to close the dropdown when clicking outside.
 * @default true
 * @param {boolean} [usePortal] - Flag to render the dropdown in a portal.
 * @default true
 * @param {UseFloatingOptions} [dropdownOptions] - Options for floating.js.
 * @param {FloatingPortalProps} [portalProps] - Props for the FloatingPortal component.
 * @param {Function} [handleClose] - Callback function to handle closing the dropdown.
 * @param {ReactNode} [children] - The content of the dropdown.
 */
export function Dropdown({
  testId = '',
  isOpen,
  referenceElement,
  className,
  placement = 'bottom-start',
  offsetNr,
  maxHeight = MAX_DROPDOWN_HEIGHT,
  forceWidthAsReference,
  closeOnClickOutside = true,
  usePortal = true,
  dropdownOptions,
  portalProps,
  handleClose,
  children,
  ...props
}: DropdownProps) {
  const { refs, floatingStyles, middlewareData } = useFloating({
    placement: placement,
    middleware: [
      flip(),
      offset(offsetNr),
      hide(),
      size({
        apply({ rects, elements, availableHeight, availableWidth }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.min(availableHeight, maxHeight)}px`,
            maxWidth: `${forceWidthAsReference ? rects.reference.width : availableWidth}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
        padding: 8,
      }),
    ],
    elements: {
      reference: referenceElement,
    },
    whileElementsMounted: autoUpdate,
    ...dropdownOptions,
  });

  closeOnClickOutside && handleClose && useOnClickOutside(refs.floating, handleClose, [referenceElement]);

  if (!isOpen) return null;

  const dropdownMarkup = (
    <div
      data-testid={testId}
      className={classNames(styles.dropdown, className)}
      ref={refs.setFloating}
      style={{ ...floatingStyles, visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible' }}
      {...props}
    >
      {children}
    </div>
  );

  return <>{usePortal ? <FloatingPortal {...portalProps}>{dropdownMarkup}</FloatingPortal> : <>{dropdownMarkup}</>}</>;
}
